import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Info } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { RoomsSection } from "./sections/Rooms";
import { MessagesSection } from "./sections/Messages";
import { StatesSection } from "./sections/States";
import { BlacklistSection } from "./sections/Blacklist";
import { RateLimiterSection } from "./sections/RateLimiter";
import { DangerSection } from "./sections/Danger";
import { ModerationLogSection } from "./sections/ModerationLog";
import { PatternBlacklistSection } from "./sections/RegexRules";

type Section =
	| "rooms"
	| "messages"
	| "moderation"
	| "pattern"
	| "states"
	| "ip"
	| "ratelimiter"
	| "danger";

const NAV: { key: Section; label: string }[] = [
	{ key: "rooms", label: "Rooms" },
	{ key: "messages", label: "Messages" },
	{ key: "moderation", label: "Moderation Log" },
	{ key: "pattern", label: "Pattern Blacklist" },
	{ key: "states", label: "States" },
	{ key: "ip", label: "IP Blacklist" },
	{ key: "ratelimiter", label: "Rate Limiter" },
	{ key: "danger", label: "Danger" },
];

const SECTION_INFO: Record<Section, { overview: string; settings: { label: string; description: string }[] }> = {
	rooms: {
		overview: "Rooms are the chat spaces users can enter, monitor, mark as VIP, or remove.",
		settings: [
			{ label: "Add room", description: "Creates a new named room that users can join." },
			{ label: "VIP", description: "Marks a room as important so it can be treated differently in the admin view." },
			{ label: "Delete", description: "Removes the room and its related chat data." },
		],
	},
	messages: {
		overview: "Messages lets you review current chat content and remove unwanted messages or authors.",
		settings: [
			{ label: "Room filter", description: "Limits the list to messages from one selected room." },
			{ label: "Delete message", description: "Removes one specific message from the chat." },
			{ label: "Delete by author", description: "Removes all visible messages sent by the same user." },
			{ label: "Ban IP", description: "Adds the sender IP to the IP blacklist and removes matching messages." },
		],
	},
	moderation: {
		overview: "Moderation Log shows recent automated moderation actions and controls AI moderation behavior.",
		settings: [
			{ label: "AI Moderation", description: "Turns OpenAI-based moderation on or off for new messages." },
			{ label: "Blocked Message Text", description: "Sets the replacement text shown when AI moderation blocks a message." },
			{ label: "Delete log", description: "Removes one moderation event from the audit list." },
			{ label: "Delete all", description: "Clears all stored moderation log entries." },
		],
	},
	pattern: {
		overview: "Pattern Blacklist blocks or masks messages using grouped regex patterns and character substitutions.",
		settings: [
			{ label: "Pattern Blacklist", description: "Turns pattern-based message checks on or off." },
			{ label: "Split Text Detection", description: "Checks several short messages together to catch users splitting blocked words." },
			{ label: "Split Window Message Count", description: "Sets how many recent short messages are checked together." },
			{ label: "Split Window Seconds", description: "Sets how long short messages stay in the split-text check window." },
			{ label: "Split Max Message Length", description: "Only messages at or below this length are added to split-text detection." },
			{ label: "Character groups", description: "Treats listed characters as equivalent, such as i, 1, l, !, and |." },
			{ label: "Pattern name", description: "Names the rule group and appears as the reason in moderation logs." },
			{ label: "Pattern(s)", description: "Stores one or more newline-separated regex patterns in the same manageable group." },
			{ label: "Action", description: "Block rejects the message; mask replaces matching text with asterisks." },
			{ label: "Enabled", description: "Turns one pattern group or character group on or off without deleting it." },
		],
	},
	states: {
		overview: "States define the room lifecycle timing used by the chat experience.",
		settings: [
			{ label: "State", description: "Names a room lifecycle step, such as warning or termination." },
			{ label: "Transition seconds", description: "Sets how long a room waits before moving to that state." },
			{ label: "Add or edit", description: "Creates a new state or updates the timing of an existing one." },
			{ label: "Delete", description: "Removes a state from the lifecycle configuration." },
		],
	},
	ip: {
		overview: "IP Blacklist blocks messages from specific network addresses.",
		settings: [
			{ label: "IP Blacklist", description: "Turns IP-based blocking on or off globally." },
			{ label: "Value", description: "The IP address to block, such as 192.168.1.1." },
			{ label: "Enabled", description: "Turns one blocked IP entry on or off without deleting it." },
			{ label: "Delete", description: "Removes an IP address from the blacklist." },
		],
	},
	ratelimiter: {
		overview: "Rate Limiter controls how often users and admins can perform sensitive actions.",
		settings: [
			{ label: "Message allowance", description: "Sets how many messages a user can send in the message time window." },
			{ label: "Message window duration", description: "Sets the length of the message rate-limit window in milliseconds." },
			{ label: "Global HTTP max", description: "Limits general HTTP requests from one source." },
			{ label: "Admin login max", description: "Limits failed or repeated admin login attempts." },
			{ label: "Create room max", description: "Limits how often one source can create new rooms." },
			{ label: "Message max content length", description: "Sets the maximum allowed message length." },
		],
	},
	danger: {
		overview: "Danger contains destructive maintenance actions that reset stored application data.",
		settings: [
			{ label: "Reset database", description: "Deletes current chat/admin data covered by the reset and restores seed records." },
			{ label: "Confirmation", description: "Requires typing the exact confirmation text before reset can run." },
		],
	},
};

const SECTION_KEYS = new Set<Section>(NAV.map((n) => n.key));

function isSection(value: string | null): value is Section {
	return value !== null && SECTION_KEYS.has(value as Section);
}

export function AdminDashboard() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [authed, setAuthed] = useState<boolean | null>(null);
	const [infoOpen, setInfoOpen] = useState(false);

	const rawSection = searchParams.get("section");
	const section: Section = isSection(rawSection) ? rawSection : "rooms";
	const activeLabel = NAV.find((n) => n.key === section)?.label ?? "Admin";
	const activeInfo = SECTION_INFO[section];

	useEffect(() => {
		api.admin.rooms.all.get().then(({ error }) => {
			if (error?.status === 401) {
				navigate("/admin/login");
			} else {
				setAuthed(true);
			}
		});
	}, []);

	function selectSection(key: Section) {
		setSearchParams({ section: key });
	}

	if (authed === null) return null;

	return (
		<div className="admin-shell min-h-screen bg-[#010403] text-[#d8fff7] flex flex-col">
			{/* Header */}
			<header className="border-b border-[#12362f] bg-[#020907]/95 px-6 py-3 flex items-center justify-between">
				<div className="flex items-center gap-3">
					<span className="text-[#8dbdb3] text-xs font-mono">ghost chat</span>
					<span className="text-[#2e665c]">/</span>
					<span className="text-[#00ffcc] text-xs font-mono uppercase tracking-widest">admin</span>
				</div>
				<Button variant="ghost" size="sm" onClick={() => navigate("/")}>
					exit
				</Button>
			</header>

			<div className="flex flex-1">
				{/* Sidebar */}
				<nav className="w-48 border-r border-[#12362f] bg-[#010706] py-4 flex flex-col gap-1 px-2 shrink-0">
					{NAV.map(({ key, label }) => (
						<button
							key={key}
							onClick={() => selectSection(key)}
							className={`text-left px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors cursor-pointer border ${
								section === key
									? "border-[#00ffcc66] text-[#00ffcc] bg-[#00ffcc10]"
									: "border-transparent text-[#8dbdb3] hover:text-[#00ffcc] hover:bg-[#00ffcc08]"
							}`}
						>
							{label}
						</button>
					))}
				</nav>

				{/* Content */}
				<main className="flex-1 p-6 overflow-auto bg-[radial-gradient(circle_at_top_right,#00ffcc08,transparent_35%)]">
					<div className="mb-6 flex items-center gap-2">
						<h2 className="text-xs font-mono uppercase tracking-widest text-[#00ffcc]">{activeLabel}</h2>
						<Button
							size="icon"
							variant="ghost"
							aria-label={`About ${activeLabel}`}
							title={`About ${activeLabel}`}
							onClick={() => setInfoOpen(true)}
						>
							<Info size={15} />
						</Button>
					</div>

					{section === "rooms" && <RoomsSection />}
					{section === "messages" && <MessagesSection />}
					{section === "moderation" && <ModerationLogSection />}
					{section === "pattern" && <PatternBlacklistSection />}
					{section === "states" && <StatesSection />}
					{section === "ip" && <BlacklistSection type="ip" />}
					{section === "ratelimiter" && <RateLimiterSection />}
					{section === "danger" && <DangerSection />}
				</main>
			</div>

			<Dialog open={infoOpen} onOpenChange={setInfoOpen}>
				<DialogContent className="max-w-xl">
					<DialogHeader>
						<DialogTitle>{activeLabel}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 text-sm">
						<p className="text-[#00ffcc] font-mono text-xs leading-relaxed">{activeInfo.overview}</p>
						<div className="space-y-3">
							{activeInfo.settings.map((setting) => (
								<div key={setting.label} className="border-t border-[#12362f] pt-3">
									<div className="text-xs font-mono uppercase tracking-widest text-[#9acfc4]">{setting.label}</div>
									<p className="mt-1 text-xs leading-relaxed text-[#d8fff7]">{setting.description}</p>
								</div>
							))}
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
